/**
 * Kabinetning umumiy karkasi: yon menyu, yuqori panel va sahifa sarlavhasi.
 *
 * Barcha modul sahifalari shu karkas ichida ochiladi — menyu, qidiruv,
 * bildirishnomalar va profil har bir sahifada bir xil joyda turadi.
 *
 * Jonli hisoblagichlar (`kuzatuv/dashboard`, o'qilmagan bildirishnomalar) shu
 * yerda bir marta so'raladi va kontekst orqali sahifalarga uzatiladi —
 * boshqaruv paneli o'sha ma'lumotni ikkinchi marta so'ramaydi.
 */
import { createContext, useContext, useEffect, useMemo, useRef, useState } from "react";
import type { ReactNode } from "react";
import { Link, NavLink, useLocation, useNavigate } from "react-router-dom";

import { useAuth } from "@/app/AuthContext";
import { kuzatuvApi } from "@/shared/api/endpoints";
import type { OperatorDashboard } from "@/shared/api/types";
import { visibleGroups } from "@/shared/data/navigation";
import { useI18n } from "@/shared/i18n";
import { useResource } from "@/shared/lib/resource";
import type { Resource } from "@/shared/lib/resource";
import { AiAssistant } from "@/shared/ui/AiAssistant";
import { LangSwitch, TextSizeButton, ThemeButton } from "@/shared/ui/Controls";
import { BrandMark, Icon } from "@/shared/ui/Icon";
import { MobileLinkButton } from "@/shared/ui/MobileLinkButton";

interface CabinetValue {
  live: Resource<OperatorDashboard> & { reload: () => void };
  unread: number;
}

const CabinetContext = createContext<CabinetValue | null>(null);

export function useCabinet(): CabinetValue {
  const ctx = useContext(CabinetContext);
  if (!ctx) throw new Error("useCabinet faqat <CabinetProvider> ichida ishlaydi");
  return ctx;
}

/**
 * Jonli hisoblagichlar provayderi.
 *
 * Marshrut darajasida (kabinet bo'limlari ustida) bir marta o'rnatiladi:
 * bo'limdan bo'limga o'tganda `kuzatuv/dashboard` qayta so'ralmaydi, karkas
 * ham, sahifalar ham bitta manbadan foydalanadi.
 */
export function CabinetProvider({ children }: { children: ReactNode }) {
  const live = useResource(() => kuzatuvApi.dashboard(), []);
  const unreadResource = useResource(() => kuzatuvApi.unreadCount(), []);
  const unread = unreadResource.data?.unread ?? 0;

  const value = useMemo<CabinetValue>(() => ({ live, unread }), [live, unread]);
  return <CabinetContext.Provider value={value}>{children}</CabinetContext.Provider>;
}

/**
 * Doimiy karkas: yon menyu va yuqori panel. Marshrut darajasida (`App.tsx`)
 * `Suspense`dan TASHQARIDA bir marta o'rnatiladi — bo'limlar orasida
 * o'tganda (birinchi marta ochilayotgan sahifa chunki hali yuklanmagan
 * bo'lsa ham) sidebar/topbar qayta o'rnatilib, "tebranib" ketmaydi, faqat
 * ichkaridagi kontent almashadi.
 */
export function CabinetShell({ children }: { children: ReactNode }) {
  const { t } = useI18n();
  const { user, signOut } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [query, setQuery] = useState("");
  const searchRef = useRef<HTMLDivElement>(null);

  const { live, unread } = useCabinet();

  const groups = useMemo(() => visibleGroups(user?.permissions), [user?.permissions]);

  /** Yuqori paneldagi qidiruv — bo'limlar bo'yicha tez o'tish. */
  const matches = useMemo(() => {
    const needle = query.trim().toLowerCase();
    if (!needle) return [];
    return groups
      .flatMap((group) => group.items.map((item) => ({ ...item, group: group.title })))
      .filter((item) => item.label.toLowerCase().includes(needle))
      .slice(0, 6);
  }, [query, groups]);

  /* Bo'lim almashganda qidiruv qutisi tozalanadi — aks holda eski so'rov va
     natijalar ro'yxati keyingi sahifada osilib qolganday ko'rinadi. */
  useEffect(() => {
    setQuery("");
  }, [location.pathname]);

  /* Tashqariga bosilganda natijalar ro'yxati yopiladi. */
  useEffect(() => {
    if (matches.length === 0) return;
    const onPointerDown = (event: MouseEvent) => {
      if (!searchRef.current?.contains(event.target as Node)) setQuery("");
    };
    document.addEventListener("mousedown", onPointerDown);
    return () => document.removeEventListener("mousedown", onPointerDown);
  }, [matches.length]);

  /** AI yordamchi `navigate` qila oladigan sahifalar — menyudagi bilan bir xil (huquqqa qarab). */
  const assistantPages = useMemo(
    () => groups.flatMap((group) => group.items.map((item) => ({ path: item.to, label: item.label }))),
    [groups],
  );

  const openFirstMatch = () => {
    if (matches.length === 0) return;
    navigate(matches[0].to);
    setQuery("");
  };

  return (
    <div className="app">
      <button
        type="button"
        className={`scrim-overlay${sidebarOpen ? " is-open" : ""}`}
        aria-label={t("action.close")}
        onClick={() => setSidebarOpen(false)}
      />

      {/* ---------------------------------------------------------- sidebar */}
      <aside className={`sidebar${sidebarOpen ? " is-open" : ""}`}>
        <div className="sidebar-head">
          <Link className="brand" to="/">
            <BrandMark size={32} className="brand-mark" spin />
            <span className="brand-text">
              <b>{t("app.name")}</b>
              <span>{t("dash.breadcrumb")}</span>
            </span>
          </Link>
        </div>

        <nav className="sidebar-nav" aria-label={t("dash.breadcrumb")}>
          {groups.map((group) => (
            <div className="nav-group" key={group.title}>
              <h4>{group.title}</h4>
              {group.items.map((item) => {
                /* Yon menyudagi hisoblagichlar — jonli ma'lumotdan. */
                const badge =
                  item.key === "events"
                    ? live.data?.active_events.total
                    : item.key === "appeals"
                      ? live.data?.appeals_pending.total
                      : undefined;
                return (
                  <NavLink
                    className={({ isActive }) => `nav-item${isActive ? " is-active" : ""}`}
                    to={item.to}
                    end={item.to === "/kabinet"}
                    key={item.to}
                    onClick={() => setSidebarOpen(false)}
                  >
                    <Icon name={item.icon} size={18} />
                    {item.label}
                    {badge ? (
                      <span className={`count${item.key === "appeals" ? "" : " is-alert"}`}>
                        {badge}
                      </span>
                    ) : null}
                  </NavLink>
                );
              })}
            </div>
          ))}
        </nav>

        <div className="sidebar-foot">
          <div className="sidebar-user">
            <Link className="sidebar-user-link" to="/kabinet/profil">
              <span className="avatar avatar-neutral" aria-hidden="true">
                <Icon name="user" size={16} />
              </span>
              <div>
                <b>{user?.full_name || "—"}</b>
                <span>{user?.position_name || user?.role_codes?.[0] || ""}</span>
              </div>
            </Link>
            <button
              type="button"
              className="icon-btn is-danger"
              onClick={() => void signOut()}
              aria-label={t("action.logout")}
              title={t("action.logout")}
            >
              <Icon name="logout" size={16} />
            </button>
          </div>
        </div>
      </aside>

      {/* ------------------------------------------------------------- main */}
      <div className="main">
        <header className="topbar">
          <button
            type="button"
            className="icon-btn sidebar-toggle"
            aria-label={t("action.menu")}
            aria-expanded={sidebarOpen}
            onClick={() => setSidebarOpen((open) => !open)}
          >
            <Icon name="menu" size={20} />
          </button>

          <div className="topbar-search" ref={searchRef}>
            <span className="input-icon">
              <Icon name="search" size={16} />
            </span>
            <label className="visually-hidden" htmlFor="global-search">
              {t("action.search")}
            </label>
            <input
              id="global-search"
              className="input"
              type="search"
              value={query}
              placeholder={t("cab.searchPh")}
              onChange={(event) => setQuery(event.target.value)}
              onKeyDown={(event) => {
                if (event.key === "Enter") openFirstMatch();
                if (event.key === "Escape") setQuery("");
              }}
              aria-expanded={matches.length > 0}
              role="combobox"
              aria-controls="global-search-results"
            />
            {matches.length > 0 ? (
              <ul className="search-results" id="global-search-results">
                {matches.map((item) => (
                  <li key={item.to}>
                    <Link to={item.to} onClick={() => setQuery("")}>
                      <Icon name={item.icon} size={16} />
                      <span>{item.label}</span>
                      <small>{item.group}</small>
                    </Link>
                  </li>
                ))}
              </ul>
            ) : null}
          </div>

          <div className="topbar-actions">
            <TextSizeButton variant="light" />
            <ThemeButton variant="light" />
            <LangSwitch variant="light" />
            <MobileLinkButton />
            <Link
              className="icon-btn"
              to="/kabinet/bildirishnomalar"
              aria-label={t("dash.notifications")}
            >
              <Icon name="bell" size={18} />
              {unread ? <span className="dot-badge">{unread > 99 ? "99+" : unread}</span> : null}
            </Link>
          </div>
        </header>

        {children}
      </div>

      <AiAssistant
        agent={{
          pages: assistantPages,
          greeting:
            "Salom! Men Smart Forestry AI yordamchisiman. Tizim holati haqida so'rang yoki sahifani ochishni buyuring — masalan: «Ijara bo'limiga kir».",
          suggestions: [
            "Hozir nechta faol hodisa bor?",
            "Muddati o'tgan murojaatlar qancha?",
            "Ijara bo'limini och",
          ],
        }}
      />
    </div>
  );
}

/**
 * Sahifa sarlavhasi va kontent maydoni — sidebar/topbar ichida, har bir
 * bo'lim o'z sarlavhasi va asboblari bilan chaqiradi. Bu qism `Suspense`
 * ICHIDA (birinchi ochilishda kechikadigan yagona qism), shuning uchun
 * bo'limlar orasida o'tganda faqat shu maydon almashadi.
 */
export function CabinetLayout({
  title,
  subtitle,
  tools,
  children,
  hideTitle = false,
}: {
  title: string;
  subtitle?: string;
  /** Sarlavha o'ngidagi boshqaruv elementlari (davr tanlash, eksport…). */
  tools?: ReactNode;
  children: ReactNode;
  /**
   * Sarlavha (`h1`), tavsif va `tools` ko'rinmaydi — sahifa o'zi shu
   * ma'lumotni boshqacha joyda (masalan xarita ustidagi suzuvchi panelda)
   * ko'rsatadi. Breadcrumb ("Kabinet > …") baribir o'z joyida qoladi —
   * navigatsiya izi har doim bir xil turishi kerak.
   */
  hideTitle?: boolean;
}) {
  const { t } = useI18n();

  return (
    <main className="content" id="main">
      <div className="page-head">
        <div>
          <div className="breadcrumb">
            <Link to="/kabinet">{t("dash.breadcrumb")}</Link>
            <Icon name="chevronRight" size={12} />
            <span>{title}</span>
          </div>
          {!hideTitle ? (
            <>
              <h1>{title}</h1>
              {subtitle ? <p className="page-sub">{subtitle}</p> : null}
            </>
          ) : null}
        </div>

        {!hideTitle && tools ? <div className="page-tools">{tools}</div> : null}
      </div>

      {children}
    </main>
  );
}
